import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { Dialog, Portal, Text } from "react-native-paper";

export interface DialogButton {
    text: string;
    style?: "cancel" | "destructive" | "default";
    onPress?: () => void;
}

interface Props {
    visible: boolean;
    title: string;
    message: string;
    buttons?: DialogButton[];
    onDismiss: () => void;
}

export function ConfirmDialog({ visible, title, message, buttons, onDismiss }: Props) {
    const resolvedButtons = buttons ?? [{ text: "OK", onPress: onDismiss }];

    return (
        <Portal>
            <Dialog visible={visible} onDismiss={onDismiss} style={styles.dialog}>
                <Dialog.Content style={styles.content}>
                    <Text style={styles.title}>{title}</Text>
                    <ScrollView
                        style={styles.messageScroll}
                        contentContainerStyle={styles.messageScrollContent}
                        showsVerticalScrollIndicator
                    >
                        <Text style={styles.message}>{message}</Text>
                    </ScrollView>
                </Dialog.Content>
                <Dialog.Actions style={styles.actions}>
                    {resolvedButtons.map((btn, i) => (
                        <TouchableOpacity
                            key={i}
                            style={[
                                styles.btn,
                                btn.style === "destructive" && styles.btnDestructive,
                                btn.style === "cancel" && styles.btnCancel
                            ]}
                            onPress={async () => {
                                onDismiss();
                                await btn.onPress?.();
                            }}
                        >
                            <Text
                                style={[
                                    styles.btnText,
                                    btn.style === "destructive" && styles.btnTextDestructive,
                                    btn.style === "cancel" && styles.btnTextCancel
                                ]}
                            >
                                {btn.text}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </Dialog.Actions>
            </Dialog>
        </Portal>
    );
}

const styles = StyleSheet.create({
    dialog: {
        borderRadius: 20,
        backgroundColor: "#FFFFFF",
        width: "90%",
        maxWidth: 380,
        alignSelf: "center"
    },
    content: {
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: 8
    },
    title: {
        fontSize: 18,
        fontWeight: "800",
        color: "#1F1547",
        marginBottom: 10
    },
    message: {
        fontSize: 14,
        color: "#6B6B8D",
        lineHeight: 20,
        flexShrink: 1
    },
    messageScroll: {
        maxHeight: 260
    },
    messageScrollContent: {
        paddingBottom: 2
    },
    actions: {
        paddingHorizontal: 16,
        paddingBottom: 16,
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
        justifyContent: "flex-end"
    },
    btn: {
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 12,
        backgroundColor: "#F5F3FF",
        minWidth: 96,
        flexShrink: 1,
        alignItems: "center"
    },
    btnDestructive: {
        backgroundColor: "#FEF2F2"
    },
    btnCancel: {
        backgroundColor: "#F5F5F4"
    },
    btnText: {
        fontSize: 14,
        fontWeight: "700",
        color: "#7C3AED",
        textAlign: "center",
        flexShrink: 1
    },
    btnTextDestructive: {
        color: "#DC2626"
    },
    btnTextCancel: {
        color: "#6B6B8D"
    }
});
